import {
    Alert,
    AlertIcon,
    Badge,
    Center,
    HStack,
    Icon,
    Link,
    Spinner,
    Text,
    Tr,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { TbCreditCard, TbExternalLink, TbFileText } from 'react-icons/tb';
import type { SubscriptionsSettingsData } from '../../hooks/useGetSettings';
import {
    useGetSubscription,
    type SubscriptionDetailItem,
    type SubscriptionDetailSummary,
} from '../../hooks/useGetSubscription';
import { useBillingPortal } from '../../hooks/useBillingPortal';
import { formatBillingAmount, formatBillingDate, formatInterval } from '../../utils/billingFormat';
import PartBayButton from '../PartBayButton';
import {
    SettingsCard,
    SettingsRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
} from './primitives';

export interface SubscriptionProps {
    data: SubscriptionsSettingsData;
}

const STATUS_COLOR: Record<string, string> = {
    active: 'green',
    trialing: 'blue',
    past_due: 'orange',
    unpaid: 'red',
    canceled: 'gray',
    incomplete: 'yellow',
    incomplete_expired: 'gray',
    paused: 'purple',
};

const statusColor = (status: string | null | undefined) => STATUS_COLOR[status ?? ''] ?? 'gray';

const humanize = (value: string | null | undefined) =>
    value ? value.replace(/_/g, ' ') : '—';

const portalUrl = (response: unknown): string | undefined => {
    if (!response || typeof response !== 'object') return undefined;
    const record = response as Record<string, unknown>;
    if (typeof record.url === 'string') return record.url;
    const nested = record.data;
    if (nested && typeof nested === 'object' && typeof (nested as { url?: unknown }).url === 'string') {
        return (nested as { url: string }).url;
    }
    return undefined;
};

const PlanItemTiers = ({ item }: { item: SubscriptionDetailItem }) => {
    const isTiered = item.billingScheme === 'tiered' && !!item.tiers?.length;
    if (!isTiered) return null;
    return (
        <VStack spacing={0.5} align="stretch" mt={1}>
            <Text fontSize="xs" color="gray.400" textTransform="capitalize">
                {item.tiersMode ? `${item.tiersMode} tiers` : 'Tiered'}
            </Text>
            {item.tiers!.map((tier, index) => (
                <Text key={index} fontSize="xs" color="gray.500">
                    {tier.upTo != null ? `Up to ${tier.upTo}` : 'Thereafter'}:{' '}
                    {tier.unitAmount != null
                        ? `${formatBillingAmount(tier.unitAmount, item.currency)}/shop`
                        : ''}
                    {tier.flatAmount != null
                        ? `${tier.unitAmount != null ? ' + ' : ''}${formatBillingAmount(tier.flatAmount, item.currency)} flat`
                        : ''}
                </Text>
            ))}
        </VStack>
    );
};

const CancelAlert = ({ subscription }: { subscription: SubscriptionDetailSummary }) => {
    const cancelDate = subscription.cancelAt ?? subscription.currentPeriodEnd ?? null;
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    const cancelsWithinSixMonths =
        !!subscription.willCancel &&
        cancelDate != null &&
        cancelDate * 1000 <= sixMonthsFromNow.getTime();

    if (subscription.isCanceled) {
        return (
            <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Canceled
                {subscription.canceledAt != null ? ` on ${formatBillingDate(subscription.canceledAt)}` : ''}
                {subscription.cancellationReason ? ` — ${humanize(subscription.cancellationReason)}` : ''}.
            </Alert>
        );
    }

    if (!cancelsWithinSixMonths) return null;

    return (
        <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {subscription.cancelAtPeriodEnd
                ? `Cancels at the end of the current period (${formatBillingDate(subscription.cancelAt ?? subscription.currentPeriodEnd)}).`
                : `Scheduled to cancel on ${formatBillingDate(subscription.cancelAt)}.`}
        </Alert>
    );
};

/**
 * Billing for the org: plan, payment method, and history. Details load from
 * GET /subscription when this tab opens. Manage billing sends the user to
 * Stripe's customer portal.
 */
const Subscription = ({ data }: SubscriptionProps) => {
    const toast = useToast();
    const billingPortal = useBillingPortal();
    const primaryId = data.subscriptions[0]?.id ?? null;
    const {
        data: billing,
        isLoading,
        isError,
        error,
    } = useGetSubscription(primaryId, !!primaryId);

    const customer = billing?.customer ?? data.customer;
    const subscriptions = billing?.subscriptions ?? [];
    const paymentHistory = billing?.paymentHistory ?? [];
    const totals = billing?.totals;
    const subscription = subscriptions[0] ?? null;

    const handleManageBilling = () => {
        billingPortal.mutate(undefined, {
            onSuccess: (response) => {
                const url = portalUrl(response);
                if (!url) {
                    toast({
                        title: 'Could not open billing portal',
                        description: 'No portal URL was returned.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                        position: 'bottom-right',
                    });
                    return;
                }
                window.location.assign(url);
            },
            onError: (err) => {
                toast({
                    title: 'Could not open billing portal',
                    description: err.message || 'Please try again.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            },
        });
    };

    const manageBillingButton = (
        <PartBayButton
            colorScheme="partbay-blue"
            variant="outline"
            size="sm"
            text="Manage billing"
            onClick={handleManageBilling}
            isLoading={billingPortal.isPending}
            loadingText="Opening..."
        />
    );

    if (!primaryId) return null;

    if (isLoading) {
        return (
            <Center py={20}>
                <Spinner size="xl" color="#4051B5" />
            </Center>
        );
    }

    if (isError || !subscription) {
        return (
            <VStack spacing={6} align="stretch" pb={16}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {(error as Error)?.message || 'Failed to load subscription details.'}
                </Alert>
                {customer && (
                    <SettingsCard title="Payment method" headerAction={manageBillingButton}>
                        <SettingsRow
                            label="Account"
                            control={
                                <Text fontSize="sm" color="gray.800">
                                    {customer.name ?? '—'}
                                </Text>
                            }
                        />
                    </SettingsCard>
                )}
            </VStack>
        );
    }

    const payment = customer?.defaultPaymentMethod;
    const upcoming = subscription.upcomingInvoice;

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <CancelAlert subscription={subscription} />

            <SettingsCard
                title="Plan"
                description={`${formatBillingAmount(subscription.recurringTotal, subscription.currency)}${formatInterval(subscription.items[0]?.interval, subscription.items[0]?.intervalCount)}`}
                headerAction={
                    <Badge colorScheme={statusColor(subscription.status)} textTransform="capitalize">
                        {humanize(subscription.status)}
                    </Badge>
                }
            >
                {subscription.items[0]?.interval !== 'month' && subscription.monthlyTotal > 0 && (
                    <SettingsRow
                        label="Monthly equivalent"
                        control={
                            <Text fontSize="sm" color="gray.700">
                                {formatBillingAmount(subscription.monthlyTotal, subscription.currency)}/mo
                            </Text>
                        }
                    />
                )}
                <SettingsRow
                    label="Current period"
                    control={
                        <Text fontSize="sm" color="gray.800">
                            {formatBillingDate(subscription.currentPeriodStart)} –{' '}
                            {formatBillingDate(subscription.currentPeriodEnd)}
                        </Text>
                    }
                />
                <SettingsRow
                    label="Started"
                    control={
                        <Text fontSize="sm" color="gray.800">
                            {formatBillingDate(subscription.startDate)}
                        </Text>
                    }
                />
                {subscription.trialEnd != null && (
                    <SettingsRow
                        label="Trial ends"
                        control={
                            <Text fontSize="sm" color="gray.800">
                                {formatBillingDate(subscription.trialEnd)}
                            </Text>
                        }
                    />
                )}
                {subscription.willCancel && subscription.cancelAt != null && (
                    <SettingsRow
                        label="Cancels on"
                        control={
                            <Text fontSize="sm" color="gray.800">
                                {formatBillingDate(subscription.cancelAt)}
                            </Text>
                        }
                    />
                )}
                {subscription.canceledAt != null && (
                    <SettingsRow
                        label="Canceled"
                        control={
                            <Text fontSize="sm" color="gray.800">
                                {formatBillingDate(subscription.canceledAt)}
                            </Text>
                        }
                    />
                )}
                {subscription.endedAt != null && (
                    <SettingsRow
                        label="Ended"
                        control={
                            <Text fontSize="sm" color="gray.800">
                                {formatBillingDate(subscription.endedAt)}
                            </Text>
                        }
                    />
                )}
                <SettingsRow
                    label="Billing"
                    control={
                        <Text fontSize="sm" color="gray.800" textTransform="capitalize">
                            {humanize(subscription.collectionMethod)}
                        </Text>
                    }
                />
            </SettingsCard>

            {upcoming && (
                <SettingsCard title="Upcoming invoice">
                    <SettingsRow
                        label="Amount due"
                        control={
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                {formatBillingAmount(upcoming.amountDue, upcoming.currency)}
                            </Text>
                        }
                    />
                    {upcoming.nextPaymentAttempt != null && (
                        <SettingsRow
                            label="Next payment"
                            control={
                                <Text fontSize="sm" color="gray.800">
                                    {formatBillingDate(upcoming.nextPaymentAttempt)}
                                </Text>
                            }
                        />
                    )}
                    <SettingsRow
                        label="Period"
                        control={
                            <Text fontSize="sm" color="gray.800">
                                {formatBillingDate(upcoming.periodStart)} –{' '}
                                {formatBillingDate(upcoming.periodEnd)}
                            </Text>
                        }
                    />
                    {upcoming.lines.map((line, index) => (
                        <SettingsRow
                            key={index}
                            label={
                                `${line.description ?? 'Line item'}${line.quantity != null && line.quantity > 1 ? ` ×${line.quantity}` : ''}`
                            }
                            control={
                                <Text fontSize="sm" color="gray.800">
                                    {formatBillingAmount(line.amount, upcoming.currency)}
                                </Text>
                            }
                        />
                    ))}
                </SettingsCard>
            )}

            <SettingsCard title="Plan items" isFlush>
                <SettingsSubTable
                    isEmbedded
                    head={
                        <>
                            <SettingsTh>Product</SettingsTh>
                            <SettingsTh>Qty</SettingsTh>
                            <SettingsTh>Unit</SettingsTh>
                            <SettingsTh>Total</SettingsTh>
                        </>
                    }
                >
                    {subscription.items.map((item) => (
                        <Tr key={item.priceId}>
                            <SettingsTd>
                                <Text fontSize="sm">{item.productName ?? '—'}</Text>
                                <PlanItemTiers item={item} />
                            </SettingsTd>
                            <SettingsTd>{item.quantity ?? 1}</SettingsTd>
                            <SettingsTd>
                                {item.unitAmount != null ? (
                                    formatBillingAmount(item.unitAmount, item.currency)
                                ) : (
                                    <Text color="gray.400">Tiered</Text>
                                )}
                            </SettingsTd>
                            <SettingsTd>{formatBillingAmount(item.lineTotal, item.currency)}</SettingsTd>
                        </Tr>
                    ))}
                </SettingsSubTable>
            </SettingsCard>

            <SettingsCard title="Payment method" headerAction={manageBillingButton}>
                <SettingsRow
                    label="Name"
                    control={
                        <Text fontSize="sm" color="gray.800">
                            {customer?.name ?? '—'}
                        </Text>
                    }
                />
                <SettingsRow
                    label="Email"
                    control={
                        <Text fontSize="sm" color="gray.800">
                            {customer?.email ?? '—'}
                        </Text>
                    }
                />
                <SettingsRow
                    label="Card on file"
                    control={
                        payment ? (
                            <HStack spacing={2}>
                                <Icon as={TbCreditCard as React.ElementType} color="gray.500" boxSize={4} />
                                <Text fontSize="sm" color="gray.800" textTransform="capitalize">
                                    {payment.brand ?? 'Card'} ···· {payment.last4 ?? '••••'}
                                </Text>
                                {payment.expMonth && payment.expYear && (
                                    <Text fontSize="xs" color="gray.400">
                                        exp {String(payment.expMonth).padStart(2, '0')}/
                                        {String(payment.expYear).slice(-2)}
                                    </Text>
                                )}
                            </HStack>
                        ) : (
                            <Text fontSize="sm" color="gray.400">
                                None on file
                            </Text>
                        )
                    }
                />
                {billing?.customer?.delinquent && (
                    <SettingsRow
                        label="Account status"
                        control={
                            <Badge colorScheme="red">Delinquent</Badge>
                        }
                    />
                )}
            </SettingsCard>

            {totals && (
                <SettingsCard title="Totals">
                    <SettingsRow
                        label="Total paid"
                        control={
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                {formatBillingAmount(totals.totalPaid, totals.currency)}
                            </Text>
                        }
                    />
                    <SettingsRow
                        label="Outstanding"
                        control={
                            <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={totals.totalOutstanding > 0 ? 'red.500' : 'gray.800'}
                            >
                                {formatBillingAmount(totals.totalOutstanding, totals.currency)}
                            </Text>
                        }
                    />
                    <SettingsRow
                        label="Invoices"
                        control={
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                {totals.invoiceCount}
                            </Text>
                        }
                    />
                </SettingsCard>
            )}

            <SettingsCard title="Payment history" isFlush>
                {paymentHistory.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={5} py={6}>
                        No payments recorded.
                    </Text>
                ) : (
                    <SettingsSubTable
                        isEmbedded
                        head={
                            <>
                                <SettingsTh>Date</SettingsTh>
                                <SettingsTh>Invoice</SettingsTh>
                                <SettingsTh>Status</SettingsTh>
                                <SettingsTh>Amount</SettingsTh>
                                <SettingsTh w="72px" />
                            </>
                        }
                    >
                        {paymentHistory.map((paymentRecord) => (
                            <Tr key={paymentRecord.id}>
                                <SettingsTd whiteSpace="nowrap">
                                    {formatBillingDate(paymentRecord.created)}
                                </SettingsTd>
                                <SettingsTd>{paymentRecord.number ?? '—'}</SettingsTd>
                                <SettingsTd>
                                    <Badge
                                        colorScheme={statusColor(paymentRecord.status)}
                                        textTransform="capitalize"
                                        fontSize="0.65rem"
                                    >
                                        {humanize(paymentRecord.status)}
                                    </Badge>
                                </SettingsTd>
                                <SettingsTd whiteSpace="nowrap">
                                    {formatBillingAmount(paymentRecord.amountPaid, paymentRecord.currency)}
                                    {paymentRecord.amountRemaining > 0 && (
                                        <Text as="span" color="red.500" fontSize="xs">
                                            {' '}
                                            (+{formatBillingAmount(paymentRecord.amountRemaining, paymentRecord.currency)} due)
                                        </Text>
                                    )}
                                </SettingsTd>
                                <SettingsTd>
                                    <HStack spacing={2} justify="flex-end">
                                        {paymentRecord.hostedInvoiceUrl && (
                                            <Link
                                                href={paymentRecord.hostedInvoiceUrl}
                                                isExternal
                                                aria-label="View invoice"
                                            >
                                                <Icon
                                                    as={TbExternalLink as React.ElementType}
                                                    color="gray.500"
                                                    _hover={{ color: '#4051B5' }}
                                                />
                                            </Link>
                                        )}
                                        {paymentRecord.invoicePdf && (
                                            <Link
                                                href={paymentRecord.invoicePdf}
                                                isExternal
                                                aria-label="Download invoice PDF"
                                            >
                                                <Icon
                                                    as={TbFileText as React.ElementType}
                                                    color="gray.500"
                                                    _hover={{ color: '#4051B5' }}
                                                />
                                            </Link>
                                        )}
                                    </HStack>
                                </SettingsTd>
                            </Tr>
                        ))}
                    </SettingsSubTable>
                )}
            </SettingsCard>
        </VStack>
    );
};

export default Subscription;
